// src/pages/insights/Reports.jsx
//
// Generates and downloads organisation financial reports (PDF/Excel) from the
// backend export endpoints, driven by a filter panel:
//
//   - Date range   → from_date / to_date (date__gte / date__lte)
//   - Category     → Contribution/Expense multi-select mapped onto the backend
//                    `sections` param (single comma-separated query param).
//                    Contribution only → contribution_* sections, Expense only
//                    → expense_* sections, both or neither → sections omitted
//                    (backend default = full report).
//   - Amount range → min_amount / max_amount (amount__gte / amount__lte)
//
// The filter fields are a draft until "Apply" commits them; "Generate Report"
// and the "Export PDF"/"Export Excel" buttons all download using the committed
// filters, so exports always reflect exactly what was generated (never a
// separate unfiltered path).
//
// Access is gated by role: Treasury, Admin, Governing Body, and the clerk
// roles. Roles are compared against the canonical backend snake_case values
// (normalization happens once in App.js). The backend itself enforces
// membership (any org the user belongs to), so this gate is frontend
// defense-in-depth (same pattern as ViewExpenses).

import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { apiDownload } from '../../api';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

const ALLOWED_ROLES = [
  'treasury',
  'senior_accounting_clerk',
  'junior_accounting_clerk',
  'admin',
  'governing_body',
];

// Backend `sections` values for the two report scopes. Omitted sections = all.
const CONTRIBUTION_SECTIONS = [
  'contribution_project',
  'contribution_payment',
  'contribution_transactions',
];
const EXPENSE_SECTIONS = [
  'expense_category',
  'expense_payment',
  'expense_transactions',
];

const DEFAULT_FILTERS = {
  fromDate: '',
  toDate: '',
  contribution: true,
  expense: true,
  minAmount: '',
  maxAmount: '',
};

const formatWait = (seconds) => {
  if (!seconds || seconds <= 0) return 'about an hour';
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const mins = Math.ceil(seconds / 60);
  if (mins < 60) return `about ${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.ceil(mins / 60);
  return `about ${hours} hour${hours === 1 ? '' : 's'}`;
};

const Reports = ({ ngo, userRole, onBack, onHome, onEditProfile }) => {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('reports');
  const { t: tc } = useTranslation('common');

  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const isAuthorized = !userRole || ALLOWED_ROLES.includes(userRole);

  const setField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setError('');
    setSuccess('');
  };

  // ── validation (runs live on the draft, gates Apply) ──────────────────────

  const amountIsValid = (value) =>
    value === '' || (!Number.isNaN(Number(value)) && Number(value) >= 0);

  const validationError = (() => {
    if (draft.fromDate && draft.toDate && draft.fromDate > draft.toDate) {
      return t('validation.dateOrder');
    }
    if (!amountIsValid(draft.minAmount)) {
      return t('validation.minAmount');
    }
    if (!amountIsValid(draft.maxAmount)) {
      return t('validation.maxAmount');
    }
    if (
      draft.minAmount !== '' &&
      draft.maxAmount !== '' &&
      Number(draft.minAmount) > Number(draft.maxAmount)
    ) {
      return t('validation.amountOrder');
    }
    return '';
  })();

  const handleApply = () => {
    if (validationError) return;
    setApplied(draft);
    setError('');
    setSuccess(t('filtersApplied'));
  };

  const handleReset = () => {
    setDraft(DEFAULT_FILTERS);
    setApplied(DEFAULT_FILTERS);
    setError('');
    setSuccess('');
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildFallbackFilename = (format) => {
    const orgName = (ngo?.name || 'organisation').replace(/\s+/g, '_');
    const { fromDate, toDate } = applied;
    let period = 'all_time';
    if (fromDate && toDate) period = `${fromDate}_${toDate}`;
    else if (fromDate) period = `from_${fromDate}`;
    else if (toDate) period = `until_${toDate}`;
    const ext = format === 'excel' ? 'xlsx' : 'pdf';
    return `report_${orgName}_${period}.${ext}`;
  };

  const handleDownload = async (format) => {
    if (!ngo?.id) {
      setError(t('errors.selectOrg'));
      return;
    }

    setGenerating(true);
    setError('');
    setSuccess('');

    try {
      const params = new URLSearchParams({ org_id: String(ngo.id) });
      if (applied.fromDate) params.set('from_date', applied.fromDate);
      if (applied.toDate) params.set('to_date', applied.toDate);
      if (applied.minAmount !== '') params.set('min_amount', applied.minAmount);
      if (applied.maxAmount !== '') params.set('max_amount', applied.maxAmount);

      // Both or neither checked → omit `sections` (backend default = full report).
      if (applied.contribution && !applied.expense) {
        params.set('sections', CONTRIBUTION_SECTIONS.join(','));
      } else if (applied.expense && !applied.contribution) {
        params.set('sections', EXPENSE_SECTIONS.join(','));
      }

      const path = format === 'excel'
        ? `/api/reports/export/excel/?${params.toString()}`
        : `/api/reports/export/pdf/?${params.toString()}`;

      const { blob, filename } = await apiDownload(path, {}, getAccessTokenSilently);
      triggerDownload(blob, filename || buildFallbackFilename(format));
      setSuccess(t('reportDownloaded'));
    } catch (err) {
      if (err?.type === 'NETWORK_ERROR') {
        setError(tc('networkError'));
      } else if (err?.status === 429) {
        setError(t('errors.rateLimit', { time: formatWait(err.retryAfter) }));
      } else if (err?.status === 400) {
        setError(err?.detail || t('errors.invalidOptions'));
      } else if (err?.status === 403) {
        setError(t('errors.noPermission'));
      } else {
        setError(t('errors.generic'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 max-w-sm w-full text-center">
          <p className="text-sm font-semibold text-gray-900 mb-2">{t('accessRestricted')}</p>
          <p className="text-xs text-gray-500 mb-6">
            {t('accessDeniedMessage')}
          </p>
          {onBack && (
            <button
              onClick={onBack}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition"
            >
              {t('goBack')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {onHome && (
          <button onClick={onHome} aria-label="Home" title="Home" className="text-blue-600 hover:text-blue-700 flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
              <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
            </svg>
          </button>
        )}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                  // Persist to backend (fire-and-forget)
                  getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
                  }).then(token => {
                    fetch(`${window.location.origin}/api/accounts/profile/update/`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ preferred_language: lang }),
                    }).catch(() => {});
                  }).catch(() => {});
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Status messages */}
        {generating && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-blue-600">            {t('generating')}</p>
          </div>
        )}

        {!generating && error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {!generating && success && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
            <p className="text-xs text-green-600">{success}</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-3">
            {t('filters')}
          </p>

          {/* Date range */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{t('dateRange')}</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                aria-label="From date"
                value={draft.fromDate}
                onChange={(e) => setField('fromDate', e.target.value)}
                disabled={generating}
                className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />
              <input
                type="date"
                aria-label="To date"
                value={draft.toDate}
                onChange={(e) => setField('toDate', e.target.value)}
                disabled={generating}
                className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />
            </div>
          </div>

          {/* Category (multi-select) */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{t('category')}</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.contribution}
                  onChange={(e) => setField('contribution', e.target.checked)}
                  disabled={generating}
                  className="accent-blue-600"
                />
                {t('contribution')}
              </label>
              <label className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.expense}
                  onChange={(e) => setField('expense', e.target.checked)}
                  disabled={generating}
                  className="accent-blue-600"
                />
                {t('expense')}
              </label>
            </div>
          </div>

          {/* Amount range */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">{t('amountRange')}</p>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                min="0"
                placeholder={tc('min')}
                aria-label="Min amount"
                value={draft.minAmount}
                onChange={(e) => setField('minAmount', e.target.value)}
                disabled={generating}
                className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />
              <input
                type="number"
                min="0"
                placeholder={tc('max')}
                aria-label="Max amount"
                value={draft.maxAmount}
                onChange={(e) => setField('maxAmount', e.target.value)}
                disabled={generating}
                className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0"
              />
            </div>
          </div>

          {validationError && (
            <p className="text-xs text-red-500 mb-3">{validationError}</p>
          )}

          {/* Apply / Reset */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApply}
              disabled={generating || Boolean(validationError)}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg py-2.5 text-sm font-medium transition"
            >
              {tc('apply')}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={generating}
              className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg py-2.5 text-sm font-medium transition"
            >
              {tc('reset')}
            </button>
          </div>
        </div>

        {/* Generate Report (primary submit) */}
        <button
          type="button"
          onClick={() => handleDownload('pdf')}
          disabled={generating}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 text-sm font-medium transition mb-4"
        >
          {generating ? t('generatingButton') : t('generateButton')}
        </button>

        {/* Export formats */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => handleDownload('pdf')}
            disabled={generating}
            className="border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg py-2.5 text-sm font-medium transition"
          >
            {t('exportPdf')}
          </button>
          <button
            type="button"
            onClick={() => handleDownload('excel')}
            disabled={generating}
            className="border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg py-2.5 text-sm font-medium transition"
          >
            {t('exportExcel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;